import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import styled, { css } from 'styled-components';

import { CareerIcon, CareerIconName } from 'atoms';
import { Colors } from 'context';
import { ICareerLessonListItem } from 'core/hooks/CareerCenter/transform/transformCareerLessons';
import { lessonHref } from 'router/careerLinks';
import { accent } from 'services/careerAccents';
import { appLanguage } from 'services/appLanguage';
import { careerLayout, careerType } from 'services/careerType';

import { CareerLinkCard } from '../CareerLinkCard';

export interface ICareerLessonRow {
  lesson: ICareerLessonListItem;
  /** `current` — крупная карточка текущего урока наверху списка. */
  variant?: 'current' | 'regular' | 'compact';
}

const OUTCOME_ICONS: Record<string, CareerIconName> = {
  cv: 'cv',
  portfolio: 'portfolio',
  profile: 'cv',
  application: 'applications',
  interview: 'interview',
};

const STATE_COPY: Record<string, { label: string; icon: CareerIconName }> = {
  not_started: { label: 'careerCenter.learning.state.not_started', icon: 'arrowRight' },
  in_progress: { label: 'careerCenter.learning.state.in_progress', icon: 'clock' },
  completed: { label: 'careerCenter.learning.state.completed', icon: 'check' },
  locked: { label: 'careerCenter.learning.state.locked', icon: 'lock' },
};

const LANGUAGE_LABEL: Record<string, string> = {
  pl: 'careerCenter.learning.language.pl',
  ru: 'careerCenter.learning.language.ru',
  en: 'careerCenter.learning.language.en',
};

/** Язык оболочки. Метка языка нужна, только если материал на ДРУГОМ языке. */
const SHELL_LANGUAGE = appLanguage();

/**
 * Карточка карьерного урока (ТЗ 0071, п. 5.2).
 *
 * Номер, название, описание, результат, длительность, состояние — и действие.
 * Доступный урок — ЦЕЛИКОМ ссылка (`CareerLinkCard`): прежняя строка была
 * кнопкой на заголовке с растянутым псевдоэлементом и не открывалась в новой
 * вкладке. Закрытый урок — не ссылка: он называет урок, который его откроет, и
 * не притворяется нажимаемым.
 *
 * Состояние считает сервер (`state`): «открывал, но не прошёл» — это
 * «W trakcie», и просмотр страницы урок не завершает.
 */
export const CareerLessonRow = memo(({ lesson, variant = 'regular' }: ICareerLessonRow) => {
  const { t } = useTranslation();
  const state = STATE_COPY[lesson.state] ?? STATE_COPY.locked;
  const isLocked = lesson.state === 'locked';
  const language =
    lesson.materialLanguage && lesson.materialLanguage !== SHELL_LANGUAGE
      ? LANGUAGE_LABEL[lesson.materialLanguage]
      : null;

  const contents: { key: string; icon: CareerIconName; label: string }[] = [
    ...(lesson.hasVideo
      ? [
          {
            key: 'video',
            icon: 'play' as CareerIconName,
            label: t('careerCenter.learning.contents.video'),
          },
        ]
      : []),
    ...(lesson.quizQuestionsCount
      ? [
          {
            key: 'quiz',
            icon: 'quiz' as CareerIconName,
            label: t('careerCenter.learning.contents.quiz', { n: lesson.quizQuestionsCount }),
          },
        ]
      : []),
    ...(lesson.hasExercise
      ? [
          {
            key: 'exercise',
            icon: 'clipboard' as CareerIconName,
            label: t('careerCenter.learning.contents.exercise'),
          },
        ]
      : []),
    ...(lesson.attachmentsCount
      ? [
          {
            key: 'files',
            icon: 'paperclip' as CareerIconName,
            label: t('careerCenter.learning.contents.files', { n: lesson.attachmentsCount }),
          },
        ]
      : []),
  ];

  const body = (
    <Layout>
      <Order $state={lesson.state} aria-hidden>
        {lesson.state === 'completed' ? (
          <CareerIcon name="check" size={20} />
        ) : lesson.state === 'locked' ? (
          <CareerIcon name="lock" size={20} />
        ) : (
          lesson.order
        )}
      </Order>
      <Body>
        <Title $variant={variant}>
          {t('careerCenter.learning.lessonNumber', { n: lesson.order })} · {lesson.title}
          {lesson.isBookmarked ? (
            <Bookmark>
              <CareerIcon
                name="starFilled"
                size={16}
                title={t('careerCenter.learning.bookmarked')}
              />
            </Bookmark>
          ) : null}
        </Title>
        {variant !== 'compact' && lesson.summary ? <Summary>{lesson.summary}</Summary> : null}
        {variant !== 'compact' && (lesson.outcomeLabel || lesson.outcomeKind) ? (
          <Outcome>
            <CareerIcon name={OUTCOME_ICONS[lesson.outcomeKind] ?? 'check'} size={18} />
            {lesson.outcomeLabel || t(`careerCenter.learning.outcome.${lesson.outcomeKind}`)}
          </Outcome>
        ) : null}
        <Meta>
          <StateTag $state={lesson.state}>
            <CareerIcon name={state.icon} size={16} />
            {t(state.label)}
          </StateTag>
          {lesson.durationMin ? (
            <span>{t('careerCenter.learning.duration', { minutes: lesson.durationMin })}</span>
          ) : null}
          {language ? <span>{t(language)}</span> : null}
        </Meta>
        {/* Состав урока — виден и на закрытом (доработка 15.09): владелец хотел
            видеть, где анкета, где тест, где видео, ещё до открытия. */}
        {contents.length ? (
          <Contents $muted={isLocked}>
            {contents.map((item) => (
              <ContentChip key={item.key}>
                <CareerIcon name={item.icon} size={16} />
                {item.label}
              </ContentChip>
            ))}
          </Contents>
        ) : null}
        {isLocked && lesson.unlockAfter ? (
          <Unlock>
            {t('careerCenter.learning.unlockAfter', {
              order: lesson.unlockAfter.order,
              title: lesson.unlockAfter.title,
            })}
          </Unlock>
        ) : null}
      </Body>
    </Layout>
  );

  if (isLocked) {
    return <LockedCard aria-disabled="true">{body}</LockedCard>;
  }
  return (
    <CareerLinkCard
      to={lessonHref(lesson.id)}
      tone={variant === 'current' ? 'current' : variant === 'compact' ? 'quiet' : 'default'}>
      {body}
    </CareerLinkCard>
  );
});

const Layout = styled.span`
  display: flex;
  gap: 16px;
  align-items: flex-start;
`;

const Order = styled.span<{ $state: string }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  ${careerType.bodyStrong()};
  background: ${({ $state }) =>
    $state === 'completed'
      ? accent('work').bg
      : $state === 'locked'
      ? Colors.bg_gray
      : accent('learning').bg};
  color: ${({ $state }) =>
    $state === 'completed'
      ? accent('work').fg
      : $state === 'locked'
      ? Colors.gray_dark
      : accent('learning').fg};
`;

const Body = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Title = styled.span<{ $variant: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  ${({ $variant }) => ($variant === 'current' ? careerType.h2() : careerType.h3())};
`;

const Bookmark = styled.span`
  display: inline-flex;
  color: ${Colors.main_dark};
`;

const Summary = styled.span`
  color: ${Colors.gray_dark};
  ${careerType.body()};
`;

const Outcome = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  ${careerType.bodyStrong()};
`;

const Meta = styled.span`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  color: ${Colors.gray_dark};
  ${careerType.meta()};
`;

const StateTag = styled.span<{ $state: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${Colors.black};
  ${careerType.metaStrong()};

  ${({ $state }) =>
    $state === 'locked' &&
    css`
      color: ${Colors.gray_dark};
    `}
`;

const Contents = styled.span<{ $muted: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  opacity: ${({ $muted }) => ($muted ? 0.75 : 1)};
`;

const ContentChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  background: ${accent('learning').bg};
  color: ${accent('learning').fg};
  ${careerType.metaStrong()};
`;

const Unlock = styled.span`
  color: ${Colors.gray_dark};
  ${careerType.meta()};
`;

const LockedCard = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: ${careerLayout.cardPadding}px;
  border: 1px dashed ${Colors.system_gray};
  border-radius: ${careerLayout.cardRadius}px;
  background: ${Colors.bg_gray};
`;
